/**
 * Security Scan Hook
 * 
 * Thin wrapper around the Zustand store for security scan progress.
 * The actual SSE connection is managed by the store, which persists
 * across route changes.
 * 
 * This hook just provides a convenient API and injects dependencies.
 */

import { useCallback } from 'react';
import { useSecurityScanStore } from '@/stores/securityScanStore';
import type { SecurityScanStatus } from '@/services/security-scanner';

// Re-export types for convenience
export type { SecurityScanStatus } from '@/services/security-scanner';

export interface UseSecurityScanOptions {
  org_id: string | number;
  user_id: string | number;
  /** @deprecated Callbacks are no longer needed - use store state instead */
  onProgress?: (status: SecurityScanStatus) => void;
  /** @deprecated Callbacks are no longer needed - use store state instead */
  onComplete?: (status: SecurityScanStatus) => void;
  /** @deprecated Callbacks are no longer needed - use store state instead */
  onError?: (error: Error) => void;
}

export interface UseSecurityScanReturn {
  startScan: () => void;
  isScanning: boolean;
  progress: number;
  status: SecurityScanStatus | null;
  error: Error | null;
  cancelScan: () => void;
}

/**
 * Hook to start and monitor security scan progress
 * 
 * Uses a Zustand store under the hood to persist SSE connections
 * across route changes. The scan will continue even when the user
 * navigates away from the Security Scanner page.
 * 
 * @example
 * const { startScan, isScanning, progress, status } = useSecurityScan({
 *   org_id: 'org-123',
 *   user_id: 'user-456',
 * });
 * 
 * // Start scan
 * startScan();
 * 
 * // Access state from store
 * console.log(isScanning, progress, status);
 */
export function useSecurityScan(options: UseSecurityScanOptions): UseSecurityScanReturn {
  const { org_id, user_id } = options;

  const state = useSecurityScanStore((store) => store.state);
  const storeStartScan = useSecurityScanStore((store) => store.startScan);
  const storeCancelScan = useSecurityScanStore((store) => store.cancelScan);

  const startScan = useCallback(() => {
    if (!org_id || !user_id) {
      console.error('❌ Cannot start scan: org_id and user_id are required');
      return;
    }
    storeStartScan(String(org_id), String(user_id));
  }, [org_id, user_id, storeStartScan]);

  // Wrap cancelScan
  const cancelScan = useCallback(() => {
    storeCancelScan();
  }, [storeCancelScan]);

  return {
    startScan,
    isScanning: state.isScanning,
    progress: state.progress,
    status: state.status,
    error: state.error,
    cancelScan,
  };
}

